import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { fr } from 'date-fns/locale';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Clock, ChevronDown } from 'lucide-react';
import 'react-day-picker/dist/style.css';

/**
 * DateTimePickerFr — French-locale day picker + native time selector.
 *
 * Replaces the browser-default <input type="datetime-local"> which renders in
 * US format ("05/27/2026, 12:00 PM") on French Chrome/Safari. Designed for the
 * EventCreationWizard but reusable wherever we need a Swiss-French date input.
 *
 * Props:
 *   value           : Date | null
 *   onChange(date)  : called whenever date/time changes (returns Date or null)
 *   label           : optional <label> above the input
 *   testId          : prefix for data-testid attributes
 *   minDate         : optional Date (default: today at 00:00)
 *
 * Output Date is timezone-naive local — convert via localInputToUtcIso when
 * sending to the backend (see /lib/calendarDate.js).
 */
const DateTimePickerFr = ({ value, onChange, label, testId = 'date-fr', minDate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [time, setTime] = useState(value ? format(value, 'HH:mm') : '09:00');

  useEffect(() => {
    if (value) setTime(format(value, 'HH:mm'));
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const pickDate = (d) => {
    if (!d) return;
    const [hh, mm] = time.split(':').map(Number);
    const combined = new Date(d);
    combined.setHours(hh || 0, mm || 0, 0, 0);
    onChange?.(combined);
  };

  const changeTime = (newTime) => {
    setTime(newTime);
    if (!value) {
      const now = new Date();
      const [hh, mm] = newTime.split(':').map(Number);
      now.setHours(hh, mm, 0, 0);
      onChange?.(now);
      return;
    }
    const next = new Date(value);
    const [hh, mm] = newTime.split(':').map(Number);
    next.setHours(hh || 0, mm || 0, 0, 0);
    onChange?.(next);
  };

  const display = value
    ? `${format(value, 'EEEE d MMMM yyyy', { locale: fr })} · ${format(value, 'HH:mm')}`
    : 'Choisir une date et une heure';

  return (
    <div ref={ref} className="relative">
      {label && (
        <label className="block text-xs font-medium text-gray-300 mb-1">{label}</label>
      )}
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((o) => !o)}
        className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 text-left"
      >
        <span className="inline-flex items-center gap-2">
          <CalendarIcon className="h-3.5 w-3.5 text-cyan-300/80" />
          <span className={value ? 'text-white' : 'text-gray-500'}>{display}</span>
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          data-testid={`${testId}-panel`}
          className="absolute z-30 mt-2 p-3 rounded-xl bg-[#0d1f35] border border-cyan-400/30 shadow-2xl"
          style={{ minWidth: 320 }}
        >
          <DayPicker
            mode="single"
            selected={value || undefined}
            onSelect={pickDate}
            locale={fr}
            weekStartsOn={1}
            disabled={minDate ? { before: minDate } : undefined}
            classNames={{
              caption: 'flex justify-between items-center mb-2 text-white text-sm font-medium',
              caption_label: 'capitalize',
              nav_button: 'p-1 rounded hover:bg-white/10 text-gray-300',
              head_cell: 'text-[10px] uppercase text-gray-500 font-medium',
              cell: 'text-center text-sm',
              day: 'w-9 h-9 rounded-lg text-gray-300 hover:bg-cyan-500/15 hover:text-cyan-200 transition',
              day_selected: '!bg-cyan-500/30 !text-cyan-100 font-semibold',
              day_today: 'border border-cyan-400/40 text-cyan-200',
              day_disabled: 'opacity-30 cursor-not-allowed hover:bg-transparent hover:text-gray-300',
              day_outside: 'opacity-30',
            }}
          />
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
            <Clock className="h-3.5 w-3.5 text-cyan-300/80" />
            <input
              data-testid={`${testId}-time`}
              type="time"
              value={time}
              onChange={(e) => changeTime(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              step="900"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-confirm`}
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-violet-500 text-white text-xs font-medium hover:opacity-90"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePickerFr;
